// ═══════════════════════════════════════════════════════════════
// useEditorialSound — hook audio éditorial pour l'univers Greg
// Encre · parchemin · or. Tintements courts, feutrés, jamais stridents.
// Web Audio API pure : aucune dépendance, aucun fichier, 100% code.
// ═══════════════════════════════════════════════════════════════

import { useRef, useCallback } from 'react';

export default function useEditorialSound({ enabled = true } = {}) {
  const ctxRef = useRef(null);

  const getCtx = useCallback(() => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctxRef.current = new AC();
    }
    // Autoplay policy : le contexte peut être "suspended" avant le 1er geste user.
    if (ctxRef.current.state === 'suspended') {
      ctxRef.current.resume().catch(() => {});
    }
    return ctxRef.current;
  }, []);

  const playTone = useCallback(({
    freq = 880,
    duration = 0.25,
    type = 'sine',
    volume = 0.06,
    attack = 0.01,
    startAt = 0,
  }) => {
    if (!enabled) return;
    try {
      const ctx = getCtx();
      if (!ctx) return;
      const t0 = ctx.currentTime + startAt;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = type;
      osc.frequency.setValueAtTime(freq, t0);

      // Enveloppe ADSR : attack rapide, decay exponentiel (feutré)
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(volume, t0 + attack);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + duration + 0.02);
    } catch (e) {
      // Fail silently — l'audio est ornement, jamais fonctionnel
    }
  }, [enabled, getCtx]);

  // ─── SONS PRÊTS À L'EMPLOI ───────────────────────────────────
  // Fréquences choisies sur la gamme pentatonique (harmonie sans fausse note)
  // C5 = 523.25 · E5 = 659.25 · G5 = 783.99 · A5 = 880 · C6 = 1046.5

  // Pop éditorial — tintement double, ouverture d'objet précieux
  // Utilisation : ouverture de modale, message reçu
  const editorialPop = useCallback(() => {
    if (!enabled) return;
    playTone({ freq: 1046.5, duration: 0.14, type: 'sine', volume: 0.035, startAt: 0 });
    playTone({ freq: 1318.5, duration: 0.2,  type: 'sine', volume: 0.04,  startAt: 0.05 });
  }, [enabled, playTone]);

  // Cloche d'or — accomplissement, module complété, validation prestige
  // Deux notes superposées (fondamentale + octave) pour une chaleur pleine
  const goldenBell = useCallback(() => {
    if (!enabled) return;
    playTone({ freq: 523.25, duration: 0.6, type: 'triangle', volume: 0.055, startAt: 0 });
    playTone({ freq: 1046.5, duration: 0.5, type: 'sine',     volume: 0.025, startAt: 0.02 });
    playTone({ freq: 1567.98, duration: 0.4, type: 'sine',    volume: 0.015, startAt: 0.04 });
  }, [enabled, playTone]);

  // Clic d'encre — sélection discrète, pill active, validation légère
  const inkClick = useCallback(() => {
    if (!enabled) return;
    playTone({ freq: 1567.98, duration: 0.08, type: 'sine', volume: 0.025 });
  }, [enabled, playTone]);

  // Balayage d'envoi — message expédié, envoi composer
  // Descente chromatique courte (C6 → A5)
  const sendSweep = useCallback(() => {
    if (!enabled) return;
    try {
      const ctx = getCtx();
      if (!ctx) return;
      const t0 = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1046.5, t0);
      osc.frequency.exponentialRampToValueAtTime(659.25, t0 + 0.28);
      gain.gain.setValueAtTime(0.05, t0);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.3);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.32);
    } catch (e) { /* silent */ }
  }, [enabled, getCtx]);

  // Feuilletage — micro-son pour hover de card importante (optionnel, très discret)
  const pageTurn = useCallback(() => {
    if (!enabled) return;
    playTone({ freq: 220, duration: 0.06, type: 'triangle', volume: 0.015, attack: 0.005 });
  }, [enabled, playTone]);

  return { editorialPop, goldenBell, inkClick, sendSweep, pageTurn };
}
